import { useRef } from 'react';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';
import { Avatar, Icon, SidePanel } from '@saviynt/design-system';

import { PoliciesSidePanelContent } from './PoliciesSidePanelContent';

import './PolicyPageSidePanel.css';

interface PolicyPageSidePanelProps {
  policy: AgentPolicy;
  isOpen: boolean;
  onClose: () => void;
  onPolicyUpdate: (data: Partial<AgentPolicy>) => void;
  onStatusChange?: (message: string) => void;
}

/**
 * Side panel for viewing details of a selected policy.
 *
 * Renders inside the Policies page side-panel wrapper, which provides
 * fixed positioning and slide-in transition. The panel itself fills
 * 100% of that wrapper.
 */
export default function PolicyPageSidePanel({
  policy,
  isOpen,
  onClose,
  onPolicyUpdate,
  onStatusChange,
}: PolicyPageSidePanelProps) {
  const sidepanelRef = useRef<HTMLDivElement>(null);

  return (
    <SidePanel
      ref={sidepanelRef}
      header={{
        title: policy.name,
        description: policy.description,
        headerIcon: (
          <Avatar size='medium'>
            <Icon kind='Policy' size='xxLarge' />
          </Avatar>
        ),
      }}
      layout={
        <div className='PolicyPageSidePanel-layoutWrapper'>
          <PoliciesSidePanelContent
            policy={policy}
            onPolicyUpdate={onPolicyUpdate}
            onStatusChange={onStatusChange}
          />
        </div>
      }
      isOpen={isOpen}
      onClose={onClose}
      isSidePanelNavigatingList
      className='PolicyPageSidePanel'
      classNames={{
        headerClassName: 'PolicyPageSidePanel-header',
        layoutClassName: 'PolicyPageSidePanel-layout',
        innerContentClassName: 'PolicyPageSidePanel-innerContent',
        footerClassName: 'PolicyPageSidePanel-footer',
      }}
    />
  );
}
